import React, { useState, useRef, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "src/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "src/components/ui/avatar";
import { X, Bell, Book, PlayCircle, Users, BookOpen, Home, LogOut, BarChart3, LayoutGrid, FileBarChart2, Menu, UserIcon, History, HelpCircle, Calendar, Clock, Award, BarChart2, FileText, ChevronUp, ChevronDown, Star, ArrowRight, ArrowUpRight, TrendingUp, Briefcase, Flame, Check, Trophy, Medal } from "lucide-react";
import MotionGlossary from "./MotionGlossary";
import { ThemeToggle } from "../components/ThemeToggle";
import { useTheme } from "../contexts/ThemeContext";
import { motion } from "framer-motion";
import { Input } from "src/components/ui/input";
import { Alert, AlertDescription } from "src/components/ui/alert";
import { fetchWithAuth } from '../utils/api';
import { useNotifications } from '../contexts/NotificationContext';
// import { CSSTransition, SwitchTransition } from 'react-transition-group';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import MobileNav from '../components/MobileNav';
import Logo from '../components/Logo';
import NotificationProvider from '../components/notifications/NotificationProvider';
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';
import LoadingBar from '../components/ui/LoadingBar';
import './Dashboard.css';
import { tryMultipleEndpoints } from '../utils/api-helpers';
import { formatMotion } from '../utils/format-helpers';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Mock data for charts and statistics
const mockStatsData = {
  totalDebates: 48,
  debatesThisMonth: 12,
  averageScore: 8.5,
  upcomingRounds: 3,
};

const mockBarData = {
  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
  datasets: [
    {
      label: 'Debates Participated',
      data: [12, 9, 15, 8, 7, 10],
      backgroundColor: 'rgba(54, 162, 235, 0.6)',
    },
  ],
};

const mockDoughnutData = {
  labels: ['Government', 'Opposition', 'Judge'],
  datasets: [
    {
      data: [12, 10, 8],
      backgroundColor: [
        'rgba(54, 162, 235, 0.6)',
        'rgba(255, 99, 132, 0.6)',
        'rgba(255, 206, 86, 0.6)',
      ],
      borderColor: [
        'rgba(54, 162, 235, 1)',
        'rgba(255, 99, 132, 1)',
        'rgba(255, 206, 86, 1)',
      ],
      borderWidth: 1,
    },
  ],
};

const mockRecentDebates = [
  {
    id: 1,
    date: '2024-06-10',
    motion: 'This house believes AI will ultimately benefit humanity',
    role: 'Government',
    outcome: 'Won',
  },
  {
    id: 2,
    date: '2024-06-05',
    motion: 'This house would ban social media for users under 18',
    role: 'Opposition',
    outcome: 'Lost',
  },
  {
    id: 3,
    date: '2024-05-27',
    motion: 'This house supports nuclear energy as a solution to climate change',
    role: 'Judge',
    outcome: 'N/A',
  },
];

// Add type definition for StatCard props
interface StatCardProps {
  icon: ReactNode;
  title: string;
  value: number | string;
  color: string;
  trend?: number;
  trendLabel?: string;
}

// Card components with enhanced design and trend indicator
const StatCard: React.FC<StatCardProps> = ({ icon, title, value, color, trend, trendLabel }) => (
  <motion.div
    whileHover={{ y: -5, transition: { duration: 0.2 } }}
    className={`p-6 rounded-xl shadow-md border-l-4 ${color}`}
    style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${color} bg-opacity-20`}>
          {icon}
        </div>
        <div className="ml-4">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</h3>
          <p className="text-2xl font-bold text-gray-800 dark:text-white">{value}</p>
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center ${trend > 0 ? 'text-green-500' : trend < 0 ? 'text-red-500' : 'text-gray-500'}`}>
          {trend > 0 ? <ChevronUp size={18} /> : trend < 0 ? <ChevronDown size={18} /> : null}
          <span className="text-sm font-medium">{Math.abs(trend)}%</span>
          {trendLabel && <span className="text-xs text-gray-500 ml-1">{trendLabel}</span>}
        </div>
      )}
    </div>
  </motion.div>
);

// Add interfaces for new components
interface EventCardProps {
  date: string;
  title: string;
  type: string;
  participants: number;
}

interface AchievementCardProps {
  title: string;
  icon: ReactNode;
  description: string;
}

// New component for upcoming events
const EventCard: React.FC<EventCardProps> = ({ date, title, type, participants }) => {
  const typeColors: Record<string, string> = {
    "British Parliamentary": "border-blue-500",
    "Asian Parliamentary": "border-purple-500",
    "Australs": "border-amber-500",
    "Practice": "border-green-500"
  };
  
  const typeColor = typeColors[type] || "border-gray-500";
  
  return (
    <div className={`p-4 border-l-4 ${typeColor} rounded-lg mb-3`}
      style={{ backgroundColor: 'var(--card-bg)' }}>
      <div className="flex justify-between">
        <div>
          <h3 className="font-medium text-gray-800 dark:text-white">{title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{type}</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-gray-800 dark:text-white">{date}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{participants} participants</div>
        </div>
      </div>
    </div>
  );
};

// Achievement card component
const AchievementCard: React.FC<AchievementCardProps> = ({ title, icon, description }) => (
  <div className="flex items-center p-3 border rounded-lg"
    style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
    <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900 text-amber-500">
      {icon}
    </div>
    <div className="ml-3">
      <h3 className="text-sm font-medium text-gray-800 dark:text-white">{title}</h3>
      <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
    </div>
  </div>
);

// Add types for debate history
interface TeamMember {
  id: number;
  username: string;
  role: string;
  speaker_score?: number;
  team?: string;
}

interface Adjudicator {
  id: number;
  username: string;
  role: string;
  rating?: number;
}

interface DebateRound {
  id: number;
  format: string;
  motion: string | { text: string };
  theme?: string;
  competition?: string;
  user_role: string;
  user_side: string;
  result: string;
  teammates: TeamMember[];
  opponents: TeamMember[];
  adjudicators: Adjudicator[];
  speaker_score?: number;
  speaker_comments?: string;
  completed_at: string;
}

interface AdjudicationRound {
  id: number;
  format: string;
  motion: string | { text: string };
  theme?: string;
  competition?: string;
  user_role: string;
  winning_side: string;
  government_team: TeamMember[];
  opposition_team: TeamMember[];
  adjudicators: Adjudicator[];
  user_rating?: number;
  completed_at: string;
  user_id?: number;
}

// Add performance stats interfaces
interface DebateStats {
  score_progression: {
    labels: string[];
    scores: number[];
  };
  speaking_time_progression: {
    labels: string[];
    times: string[];
    seconds: number[];
  };
  roles_distribution: Record<string, number>;
  win_loss: {
    wins: number;
    losses: number;
    total_debates: number;
  };
  total_debates: number;
  avg_score?: number;
}

interface AdjudicationStats {
  rating_progression: {
    labels: string[];
    ratings: number[];
  };
  roles_distribution: Record<string, number>;
  total_rounds: number;
  avg_rating?: number;
}

// Add a utility function to transform debate history data to performance stats format
const transformDebateHistoryToStats = (historyData: any) => {
  if (!historyData) {
    console.log("No history data to transform");
    return null;
  }
  
  console.log("Transforming history data:", historyData);
  
  // Initialize result structure with properly typed arrays and objects
  const result: {
    debate_stats: {
      score_progression: { 
        labels: string[], 
        scores: number[] 
      },
      speaking_time_progression: {
        labels: string[],
        times: string[],
        seconds: number[]
      },
      roles_distribution: Record<string, number>,
      win_loss: {
        wins: number,
        losses: number,
        total_debates: number
      },
      total_debates: number,
      avg_score?: number
    },
    adjudication_stats: {
      rating_progression: { 
        labels: string[], 
        ratings: number[] 
      },
      roles_distribution: Record<string, number>,
      total_rounds: number,
      avg_rating?: number
    }
  } = {
    debate_stats: {
      score_progression: { 
        labels: [], 
        scores: [] 
      },
      speaking_time_progression: {
        labels: [],
        times: [],
        seconds: []
      },
      roles_distribution: {},
      win_loss: {
        wins: 0,
        losses: 0,
        total_debates: 0
      },
      total_debates: 0
    },
    adjudication_stats: {
      rating_progression: { 
        labels: [], 
        ratings: [] 
      },
      roles_distribution: {},
      total_rounds: 0
    }
  };
  
  // Check if user_performance is available (from user_debate_history_simple)
  if (historyData.user_performance) {
    const userPerf = historyData.user_performance;
    
    // Use the calculated average score if available
    if (userPerf.avg_speaker_score !== undefined) {
      result.debate_stats.avg_score = userPerf.avg_speaker_score;
    }
    
    // Use the roles distribution if available
    if (userPerf.roles) {
      result.debate_stats.roles_distribution = userPerf.roles;
    }
    
    // Use the win/loss data if available
    if (userPerf.win_loss) {
      result.debate_stats.win_loss.wins = userPerf.win_loss.wins || 0;
      result.debate_stats.win_loss.losses = userPerf.win_loss.losses || 0;
      result.debate_stats.win_loss.total_debates = 
        (userPerf.win_loss.wins || 0) + (userPerf.win_loss.losses || 0);
    }
    
    // Use the total debates if available
    if (userPerf.total_debates !== undefined) {
      result.debate_stats.total_debates = userPerf.total_debates;
    }
  }
  
  // Process debate history if available
  if (Array.isArray(historyData?.debate_history)) {
    try {
      // Sort by date descending to get most recent first
      const sortedDebates = [...historyData.debate_history].sort((a, b) => {
        const dateA = a?.completed_at ? new Date(a.completed_at).getTime() : 0;
        const dateB = b?.completed_at ? new Date(b.completed_at).getTime() : 0;
        return dateB - dateA;
      });
      
      // If total_debates isn't set from user_performance, set it from history length
      if (!result.debate_stats.total_debates) {
        result.debate_stats.total_debates = sortedDebates.length;
      }
      
      // Process each debate for stats
      sortedDebates.forEach(debate => {
        if (!debate) return; // Skip null or undefined entries
        
        // Add to score progression (reversed to show chronological order)
        if (debate.completed_at && debate.speaker_score) {
          result.debate_stats.score_progression.labels.unshift(
            new Date(debate.completed_at).toLocaleDateString()
          );
          result.debate_stats.score_progression.scores.unshift(
            debate.speaker_score || 0
          );
        }
        
        // Count role distribution - always add to the distribution regardless of existing data
        if (debate.user_role) {
          if (!result.debate_stats.roles_distribution[debate.user_role]) {
            result.debate_stats.roles_distribution[debate.user_role] = 0;
          }
          result.debate_stats.roles_distribution[debate.user_role]++;
        }
        
        // Count wins/losses if not already populated from user_performance
        if (debate.result && 
            result.debate_stats.win_loss.wins === 0 && 
            result.debate_stats.win_loss.losses === 0) {
          if (debate.result === 'Won') {
            result.debate_stats.win_loss.wins++;
          } else if (debate.result === 'Lost') {
            result.debate_stats.win_loss.losses++;
          }
        }
      });

      // Update total debates in win_loss too for consistency
      if (result.debate_stats.win_loss.total_debates === 0) {
        result.debate_stats.win_loss.total_debates = 
          result.debate_stats.win_loss.wins + result.debate_stats.win_loss.losses;
      }

      // Calculate average score if not already provided
      if (result.debate_stats.avg_score === undefined && result.debate_stats.score_progression.scores.length > 0) {
        const sum = result.debate_stats.score_progression.scores.reduce((a, b) => a + b, 0);
        result.debate_stats.avg_score = parseFloat((sum / result.debate_stats.score_progression.scores.length).toFixed(1));
      }
      
      // Ensure we have an avg_score value for display if there are any debates at all
      if (result.debate_stats.avg_score === undefined && result.debate_stats.total_debates > 0) {
        // Use a reasonable default if we have debates but no scores
        result.debate_stats.avg_score = 75.0;
      }
    } catch (error) {
      console.error("Error processing debate history:", error);
    }
  }
  
  // Process adjudication history if available
  if (Array.isArray(historyData?.adjudication_history)) {
    try {
      // Sort by date descending
      const sortedAdjudications = [...historyData.adjudication_history].sort((a, b) => {
        const dateA = a?.completed_at ? new Date(a.completed_at).getTime() : 0;
        const dateB = b?.completed_at ? new Date(b.completed_at).getTime() : 0;
        return dateB - dateA;
      });
      
      result.adjudication_stats.total_rounds = sortedAdjudications.length;
      
      // Process each adjudication for stats
      sortedAdjudications.forEach(adj => {
        if (!adj) return; // Skip null or undefined entries
        
        // Add to rating progression (reversed for chronological order)
        if (adj.completed_at) {
          result.adjudication_stats.rating_progression.labels.unshift(
            new Date(adj.completed_at).toLocaleDateString()
          );
          result.adjudication_stats.rating_progression.ratings.unshift(
            adj.user_rating || 0
          );
        }
        
        // Count role distribution
        if (adj.user_role) {
          if (!result.adjudication_stats.roles_distribution[adj.user_role]) {
            result.adjudication_stats.roles_distribution[adj.user_role] = 0;
          }
          result.adjudication_stats.roles_distribution[adj.user_role]++;
        }
      });

      // Calculate average rating
      if (result.adjudication_stats.rating_progression.ratings.length > 0) {
        const sum = result.adjudication_stats.rating_progression.ratings.reduce((a, b) => a + b, 0);
        result.adjudication_stats.avg_rating = parseFloat((sum / result.adjudication_stats.rating_progression.ratings.length).toFixed(1));
      }
    } catch (error) {
      console.error("Error processing adjudication history:", error);
    }
  }
  
  return result;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("main");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const { pageTransition } = useTheme();
  const notificationRef = useRef<HTMLDivElement>(null);
  const [userIsAdmin, setUserIsAdmin] = useState(false);
  const [username, setUsername] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalDebates: 48,
    winRate: 65,
    avgScore: 8.5,
    upcomingEvents: 3
  });
  const [roundCode, setRoundCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [adminStats, setAdminStats] = useState({
    total_users: 0,
    active_users: 0,
    total_rounds: 0,
    active_rounds: 0,
    total_motions: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  // Add state for mobile admin stats expansion
  const [adminStatsExpanded, setAdminStatsExpanded] = useState(false);
  
  // Add state for latest round
  const [latestRound, setLatestRound] = useState<DebateRound | AdjudicationRound | null>(null);
  const [latestRoundLoading, setLatestRoundLoading] = useState(false);
  const [latestRoundError, setLatestRoundError] = useState<string | null>(null);
  
  // Add state for performance stats
  const [debateStats, setDebateStats] = useState<DebateStats | null>(null);
  const [adjudicationStats, setAdjudicationStats] = useState<AdjudicationStats | null>(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceError, setPerformanceError] = useState<string | null>(null);
  const [showRoleStatsType, setShowRoleStatsType] = useState<'debate' | 'adjudication'>('debate');
  
  // Add interval ref for role stats toggle
  const roleStatsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Mock performance data
  const performanceData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Speaking Score',
        data: [7.2, 7.5, 8.0, 7.8, 8.2, 8.7],
        borderColor: 'rgba(66, 153, 225, 0.8)',
        backgroundColor: 'rgba(66, 153, 225, 0.2)',
        fill: true,
        tension: 0.4,
      }
    ]
  };
  
  // Mock role distribution data
  const roleData = {
    labels: ['Government', 'Opposition', 'Judge'],
    datasets: [
      {
        data: [18, 16, 14],
        backgroundColor: [
          'rgba(66, 153, 225, 0.7)',
          'rgba(245, 101, 101, 0.7)',
          'rgba(246, 173, 85, 0.7)',
        ],
        borderWidth: 0,
      }
    ]
  };
  
  // Mock recent debates
  const recentDebates = [
    {
      id: 1,
      date: '2024-06-15',
      motion: 'This house believes AI will ultimately benefit humanity',
      role: 'Government',
      position: 'PM',
      outcome: 'Won',
      score: 8.7
    },
    {
      id: 2,
      date: '2024-06-08',
      motion: 'This house would ban social media for users under 18',
      role: 'Opposition',
      position: 'LO',
      outcome: 'Lost',
      score: 8.2
    },
    {
      id: 3,
      date: '2024-05-30',
      motion: 'This house supports nuclear energy as a solution to climate change',
      role: 'Judge',
      position: 'Chair',
      outcome: 'N/A',
      score: 'N/A'
    }
  ];
  
  // Mock upcoming events
  const upcomingEvents = [
    {
      id: 1,
      date: 'Jun 25, 2024',
      title: 'Regional Debate Championship',
      type: 'British Parliamentary',
      participants: 64
    },
    {
      id: 2,
      date: 'Jul 2, 2024',
      title: 'Weekly Practice Round',
      type: 'Practice',
      participants: 16
    },
    {
      id: 3,
      date: 'Jul 15, 2024',
      title: 'Inter-University Challenge',
      type: 'Asian Parliamentary',
      participants: 32
    }
  ];
  
  // Mock achievements
  const achievements = [
    {
      id: 1,
      title: 'Perfect Attendance',
      icon: <Check size={16} />,
      description: 'Attended all scheduled debates this season'
    },
    {
      id: 2,
      title: 'Rising Star',
      icon: <Star size={16} />,
      description: 'Improved score by 20% in the last month'
    },
    {
      id: 3,
      title: 'Consistent Performer',
      icon: <TrendingUp size={16} />,
      description: 'Maintained above 8.0 average for 3 months'
    }
  ];

  // Fetch admin stats
  const fetchAdminStats = async () => {
    if (!userIsAdmin) return;
    
    try {
      setStatsLoading(true);
      setStatsError(null);
      
      // Try multiple endpoints to ensure one works
      let statsResponse = null;
      let error = null;
      
      // List of possible endpoints to try
      const endpoints = [
        '/api/admin/dashboard-stats/',
        '/api/dashboard/stats/',
      ];
      
      // Try each endpoint until one works
      for (const endpoint of endpoints) {
        try {
          console.log(`Attempting to fetch stats from ${endpoint}`);
          statsResponse = await fetchWithAuth(endpoint, {}, 0, false);
          if (statsResponse) {
            console.log('Dashboard stats fetched successfully:', statsResponse);
            break;
          }
        } catch (e) {
          console.log(`Failed to fetch from ${endpoint}:`, e);
          error = e;
        }
      }
      
      if (statsResponse) {
        setAdminStats(statsResponse);
      } else {
        throw error || new Error('Failed to fetch dashboard stats from all endpoints');
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      setStatsError('Failed to load dashboard statistics');
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch user data when component mounts
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        
        const username = localStorage.getItem('username') || '';
        const isAdmin = localStorage.getItem('is_admin') === 'true';
    
        setUsername(username);
        setUserIsAdmin(isAdmin);
        
        // Simulate network request
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsLoading(false);
      }
    };
    
    checkAdminStatus();
  }, [navigate]);

  // Fetch admin stats when admin status changes or component mounts
  useEffect(() => {
    if (userIsAdmin && !isLoading) {
      console.log('Fetching admin stats because userIsAdmin changed to:', userIsAdmin);
      fetchAdminStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsAdmin, isLoading]);
  
  // Set admin stats expanded state based on screen size
  useEffect(() => {
    const setExpandedState = () => {
      // On desktop (> 768px), stats are expanded by default
      // On mobile (≤ 768px), stats are collapsed by default
      const isDesktop = window.innerWidth > 768;
      setAdminStatsExpanded(isDesktop);
    };
    
    // Set initial state
    setExpandedState();
    
    // Add event listener for window resize
    window.addEventListener('resize', setExpandedState);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', setExpandedState);
    };
  }, []);
  
  // Refresh stats periodically (every 60 seconds)
  useEffect(() => {
    if (!userIsAdmin) return;
    
    console.log('Setting up admin stats refresh interval');
    const refreshInterval = setInterval(() => {
      console.log('Auto-refreshing admin stats');
      fetchAdminStats();
    }, 60000); // 60 seconds
    
    return () => {
      console.log('Clearing admin stats refresh interval');
      clearInterval(refreshInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsAdmin]);

  // Add a more robust avatar loading mechanism
  useEffect(() => {
    // Load avatar on initial render and when window gets focus
    const loadAvatar = async () => {
      try {
        // 1. First check localStorage for immediate display
        const storedAvatar = localStorage.getItem("avatar_url");
        if (storedAvatar) {
          setAvatarUrl(storedAvatar);
        }
        
        // 2. Then try to get the most up-to-date version from server
        const username = localStorage.getItem("username");
        if (!username) return;
        
        const endpoints = [
          `/api/users/${username}/`,
          `/api/users/me/`,
          `/users/${username}/`,
          `/users/profile/`
        ];
        
        for (const endpoint of endpoints) {
          try {
            console.log(`Fetching user data from ${endpoint} to update avatar`);
            const userData = await fetchWithAuth(endpoint);
            
            if (userData && userData.avatar_url) {
              console.log(`Got avatar from ${endpoint}:`, userData.avatar_url);
              
              // Only update if different to avoid unnecessary re-renders
              if (userData.avatar_url !== storedAvatar) {
                setAvatarUrl(userData.avatar_url);
                localStorage.setItem("avatar_url", userData.avatar_url);
                console.log("Avatar updated from server");
              }
              break;
            }
          } catch (err) {
            // Silent fail for individual endpoints
          }
        }
      } catch (err) {
        console.error("Error fetching avatar:", err);
      }
    };

    // Set up event listeners
    window.addEventListener("focus", loadAvatar);
    
    // Handle localStorage changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "avatar_url" && e.newValue) {
        setAvatarUrl(e.newValue);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    
    // Load avatar immediately on mount
    loadAvatar();
    
    // Set up a periodic check every 5 minutes for very long sessions
    const periodicCheck = setInterval(loadAvatar, 5 * 60 * 1000);
    
    // Clean up
    return () => {
      window.removeEventListener("focus", loadAvatar);
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(periodicCheck);
    };
  }, []);

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setNotificationOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("is_admin");
    localStorage.removeItem("username");
    navigate("/login");
  };

  // Update the getActiveComponent function to ensure correct tabs are shown
const getActiveComponent = () => {
  switch(activeTab) {
    case "main":
      return <MainDashboard userIsAdmin={userIsAdmin} />;
    case "motions":
      return <MotionGlossary />;
    case "practice":
      return <PracticeMaterials />;
    default:
      return <MainDashboard userIsAdmin={userIsAdmin} />;
  }
};

  // Inside your Dashboard component, add:
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  // Define sidebar items with correct navigation paths
  const sidebarItems = [
    { 
      icon: Home, 
      label: "Dashboard", 
      onClick: () => {
        pageTransition(() => navigate("/dashboard"));
        setActiveTab("main"); 
      }
    },
    { 
      icon: Book, 
      label: "Motion Gallery", 
      onClick: () => {
        setActiveTab("motions");
        setSidebarOpen(false);
      }
    },
    { 
      icon: UserIcon, // or an appropriate icon for Practice
      label: "Practice Materials", 
      onClick: () => {
        setActiveTab("practice");
        setSidebarOpen(false);
      }
    },
    { 
      icon: History, 
      label: "Debate History", 
      onClick: () => {
        pageTransition(() => navigate("/debate-history"));
      }
    },
    { 
      icon: HelpCircle,
      label: "User Guide",
      onClick: () => pageTransition(() => navigate('/user-guide')),
    },
  ];

  const handleJoinRound = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    // Validate input (6 characters, all caps)
    const code = roundCode.trim().toUpperCase();
    if (!code) {
      setError("Please enter a round code");
      return;
    }
    
    if (code.length !== 6) {
      setError("Round code must be 6 characters");
      return;
    }
    
    // Regular expression to check if code contains only alphanumeric characters
    if (!/^[A-Z0-9]{6}$/.test(code)) {
      setError("Round code must contain only letters and numbers");
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Try multiple endpoints for checking if round exists
      let roundExists = false;
      let roundData = null;
      
      // Array of endpoints to try for checking if round exists
      const checkEndpoints = [
        `/api/rounds/check/${code}`,
        `/api/round/check/${code}`,
        `/rounds/check/${code}`
      ];
      
      for (const endpoint of checkEndpoints) {
        try {
          console.log(`Trying to check round with endpoint: ${endpoint}`);
          roundData = await fetchWithAuth(endpoint);
          roundExists = true;
          console.log("Round check successful:", roundData);
          break;
        } catch (err) {
          console.log(`Round check failed for endpoint ${endpoint}`);
        }
      }
      
      if (!roundExists) {
        console.log("Round check failed for all endpoints, proceeding directly to join");
      }
      
      // Try multiple endpoints for joining the round
      let joinSuccess = false;
      let response = null;
      
      // Array of endpoints to try for joining
      const joinEndpoints = [
        '/api/rounds/join/',
        '/api/round/join/',
        '/rounds/join/'
      ];
      
      for (const endpoint of joinEndpoints) {
        try {
          console.log(`Trying to join round with endpoint: ${endpoint}`);
          response = await fetchWithAuth(endpoint, {
            method: 'POST',
            body: JSON.stringify({ 
              round_code: code,
              client_version: "2.0"
            })
          });
          
          joinSuccess = true;
          console.log("Round join successful:", response);
          break;
        } catch (err) {
          console.log(`Join failed for endpoint ${endpoint}`);
        }
      }
      
      if (!joinSuccess) {
        throw new Error("Failed to join round. The round code may be invalid or the round has ended.");
      }

      // Success! Set success message and redirect
      setSuccess("Successfully joined the round!");
      setError(null);
      
      // Get the round ID from the response for navigation
      const roundId = response?.round?.id || response?.round_id || response?.id;
      
      if (!roundId) {
        throw new Error("Invalid response from server");
      }
      
      // Redirect to the waiting room for the round
      setTimeout(() => {
        navigate(`/round/${roundId}/setup`);
      }, 1000);
      
    } catch (err: any) {
      // Handle specific error cases
      console.error("Error joining round:", err);
      
      if (err.message && err.message.includes("completed")) {
        setError("This round has already been completed and cannot be joined");
      } else if (err.message && err.message.includes("column")) {
        // Database schema error - provide a user-friendly message
        setError("Unable to join round. Please try again or contact support.");
      } else {
        setError(err.message || "Failed to join round. Please verify the code is correct.");
      }
      setSuccess(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Add effect to fetch latest debate round
  useEffect(() => {
    const fetchLatestRound = async () => {
      try {
        setLatestRoundLoading(true);
        setLatestRoundError(null);
        
        // Get user ID from localStorage
        const userId = localStorage.getItem('userId');
        if (!userId) {
          console.warn('No userId found in localStorage');
          setLatestRoundLoading(false);
          return;
        }
        
        // Define endpoints to try
        const endpointOptions = [
          `/api/users/debate-history/simple/?userId=${userId}&type=personal&limit=1`,
          `/users/debate-history/simple/?userId=${userId}&type=personal&limit=1`,
          `/api/users/history/?userId=${userId}&type=personal&limit=1`
        ];
        
        try {
          // Fetch data from endpoints
          const data = await tryMultipleEndpoints(endpointOptions);
          console.log('Successfully retrieved history data:', data);
          
          // Get latest round from either debate or adjudication history
          let latestRound = null;
          let latestDate = null;
          
          if (data.debate_history && data.debate_history.length > 0) {
            const latestDebate = data.debate_history[0];
            latestDate = new Date(latestDebate.completed_at);
            latestRound = latestDebate;
          }
          
          if (data.adjudication_history && data.adjudication_history.length > 0) {
            const latestAdjudication = data.adjudication_history[0];
            const adjudicationDate = new Date(latestAdjudication.completed_at);
            
            // If there's no debate history or this is more recent, use it
            if (!latestDate || adjudicationDate > latestDate) {
              latestRound = latestAdjudication;
            }
          }
          
          setLatestRound(latestRound);
        } catch (error) {
          console.error('Error fetching latest round:', error);
          setLatestRoundError('Failed to load recent debate information');
        }
      } catch (error) {
        console.error('Error in fetchLatestRound:', error);
        setLatestRoundError('Failed to load recent debate information');
      } finally {
        setLatestRoundLoading(false);
      }
    };
    
    if (!isLoading) {
      fetchLatestRound();
    }
  }, [isLoading]);

  // Update state for toggling between debater and adjudicator stats
  const [showStatsType, setShowStatsType] = useState<'debate' | 'adjudication'>('debate');
  const statsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Add state for animation trigger
  const [animateTrigger, setAnimateTrigger] = useState(0);

  // Update effect to fetch performance stats
  useEffect(() => {
    const fetchPerformanceStats = async () => {
      try {
        setPerformanceLoading(true);
        setPerformanceError(null);
        
        // Get user ID from localStorage
        const userId = localStorage.getItem('userId');
        if (!userId) {
          console.warn('No userId found in localStorage for performance stats');
          setPerformanceLoading(false);
          return;
        }
        
        // Define endpoints to try
        const endpointOptions = [
          `/api/users/debate-history/simple/?userId=${userId}&type=personal`,
          `/users/debate-history/simple/?userId=${userId}&type=personal`,
          `/api/users/history/?userId=${userId}&type=personal`
        ];
        
        try {
          // Fetch data from endpoints
          const data = await tryMultipleEndpoints(endpointOptions);
          console.log('Successfully retrieved history data for performance stats:', data);
          
          // Transform data to stats format
          const statsData = transformDebateHistoryToStats(data);
          if (statsData) {
            console.log('Transformed performance data:', statsData);
            setDebateStats(statsData.debate_stats);
            setAdjudicationStats(statsData.adjudication_stats);
            
            // Set up interval to toggle between debate and adjudication stats
            if (statsIntervalRef.current) {
              clearInterval(statsIntervalRef.current);
            }
            
            // Use a longer interval (8 seconds) to give users more time to see each view
            statsIntervalRef.current = setInterval(() => {
              setShowStatsType(prev => {
                // Trigger animation by incrementing the counter
                setAnimateTrigger(count => count + 1);
                
                // Use a timeout to synchronize role stats change with a slight delay
                setTimeout(() => {
                  setShowRoleStatsType(prev === 'debate' ? 'adjudication' : 'debate');
                }, 300); // Small delay for staggered animation effect
                
                return prev === 'debate' ? 'adjudication' : 'debate';
              });
            }, 8000); // Increase from 5s to 8s for better visibility
          }
        } catch (error) {
          console.error('Error fetching performance stats:', error);
          setPerformanceError('Failed to load performance statistics');
        }
      } catch (error) {
        console.error('Error in fetchPerformanceStats:', error);
        setPerformanceError('Failed to load performance statistics');
      } finally {
        setPerformanceLoading(false);
      }
    };
    
    if (!isLoading) {
      fetchPerformanceStats();
    }
    
    // Clean up interval on component unmount
    return () => {
      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current);
      }
      if (roleStatsIntervalRef.current) {
        clearInterval(roleStatsIntervalRef.current);
      }
    };
  }, [isLoading]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-blue-500 border-b-blue-500 border-opacity-50"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <LoadingBar isLoading={isLoading} />
      
      <h1 className="page-title">Dashboard</h1>
      
      <div className="dashboard-welcome">
        <h2>Welcome, {username || 'User'}!</h2>
        <p>Here's an overview of your debate activity and available rounds.</p>
      </div>
      
      {/* Admin Overview Section (if user is admin) */}
      {userIsAdmin && (
        <div className="card admin-dashboard-card full-width">
          <div className="admin-header">
            <h3>Admin Overview</h3>
            <Button 
              className="admin-stats-toggle-btn"
              onClick={() => setAdminStatsExpanded(!adminStatsExpanded)}
              variant="outline"
              size="sm"
            >
              {adminStatsExpanded ? 'Hide Stats Numbers' : 'Show Stats Numbers'} 
              {adminStatsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </Button>
          </div>
          
          <div className="admin-overview-content">
            {statsError && (
              <div className="error-message">
                {statsError}
                <button 
                  onClick={fetchAdminStats}
                  className="ml-2 text-sm underline"
                >
                  Retry
                </button>
              </div>
            )}
            
            <div className={`admin-stats-grid ${adminStatsExpanded ? 'expanded' : ''}`}>
              <div className="admin-stat-box">
                <div className="admin-stat-value">
                  {statsLoading ? <span className="loading-indicator">...</span> : adminStats.total_users}
                </div>
                <div className="admin-stat-label">Total Users</div>
              </div>
              
              <div className="admin-stat-box">
                <div className="admin-stat-value">
                  {statsLoading ? <span className="loading-indicator">...</span> : adminStats.active_users}
                </div>
                <div className="admin-stat-label">Active Users</div>
              </div>
              
              <div className="admin-stat-box">
                <div className="admin-stat-value">
                  {statsLoading ? <span className="loading-indicator">...</span> : adminStats.total_rounds}
                </div>
                <div className="admin-stat-label">Total Rounds</div>
              </div>
              
              <div className="admin-stat-box">
                <div className="admin-stat-value">
                  {statsLoading ? <span className="loading-indicator">...</span> : adminStats.active_rounds}
                </div>
                <div className="admin-stat-label">Active Rounds</div>
              </div>
              
              <div className="admin-stat-box">
                <div className="admin-stat-value">
                  {statsLoading ? <span className="loading-indicator">...</span> : adminStats.total_motions}
                </div>
                <div className="admin-stat-label">Total Motions</div>
              </div>
            </div>
            
            <div className="admin-actions">
              <Button 
                className="btn-create-round"
                onClick={() => navigate('/admin/rounds/new')}
              >
                <Calendar size={18} />
                Create Round
              </Button>
              
              <Button 
                className="btn-round-management"
                onClick={() => navigate('/admin/rounds')}
              >
                <Calendar size={18} />
                Round Management
              </Button>
              
            <Button 
                className="btn-manage-users"
                onClick={() => navigate('/user-management')}
            >
                <Users size={18} />
                Manage Users
            </Button>
          
            <Button 
                className="btn-manage-motions"
                onClick={() => navigate('/admin/motions')}
            >
                <FileText size={18} />
                Manage Motions
            </Button>
              
              <Button 
                className="btn-notification-management"
                onClick={() => navigate('/admin/notifications')}
              >
                <Bell size={18} />
                Manage Notifications
              </Button>
              
            <Button 
                className="btn-activity-logs"
                onClick={() => navigate('/admin/logs')}
            >
                <FileBarChart2 size={18} />
                Activity Logs
            </Button>
            
              <Button 
                className="btn-materials-management"
                onClick={() => navigate('/admin/materials')}
              >
                <BookOpen size={18} />
                Materials Management
              </Button>
              </div>
          </div>
        </div>
      )}
      
      {/* Main Dashboard Content */}
      <div className="dashboard-grid">
        {/* Main Content Column */}
        <div className="dashboard-main">
          {/* Active Rounds */}
          <div className="card">
            <div className="card-header">
              <h3>Active Rounds</h3>
              <div className="join-round-form">
                <form onSubmit={handleJoinRound}>
                  <Input
                    type="text"
                    placeholder="Enter round code"
                    value={roundCode}
                    onChange={(e) => setRoundCode(e.target.value.toUpperCase())}
                    maxLength={6}
                    pattern="[A-Z0-9]{6}"
                    title="6-character alphanumeric code"
                  />
                  <Button type="submit">Join</Button>
                </form>
              </div>
            </div>
            <div className="card-content">
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
              {success && (
                <div className="success-message">
                  {success}
                </div>
              )}
              <div className="no-data-message">
                <p>Enter a round code above to join a debate.</p>
              </div>
            </div>
          </div>
              
          {/* Recent Debates */}
          <div className="card">
            <div className="card-header">
              <h3>Recent Debates</h3>
              <Button 
                className="view-all-link"
                onClick={() => navigate('/debate-history')}
              >
                View All
              </Button>
            </div>
            <div className="card-content">
              {latestRoundLoading && (
                <div className="loading-indicator">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-t-blue-500 border-b-blue-500 border-opacity-50 mx-auto"></div>
                  <p className="text-center mt-2">Loading recent debate...</p>
                </div>
              )}
              
              {latestRoundError && (
                <div className="error-message">
                  {latestRoundError}
                </div>
              )}
              
              {!latestRoundLoading && !latestRoundError && latestRound ? (
                <div className="recent-debate-item">
                  {'user_role' in latestRound && (
                    <div className={`role-badge ${latestRound.user_role.toLowerCase().includes('chair') || latestRound.user_role.toLowerCase().includes('adjudicator') || latestRound.user_role.toLowerCase().includes('panelist') || latestRound.user_role.toLowerCase().includes('trainee') ? 'adjudicator' : 'debater'}`}>
                      {latestRound.user_role.toLowerCase().includes('chair') || latestRound.user_role.toLowerCase().includes('adjudicator') || latestRound.user_role.toLowerCase().includes('panelist') || latestRound.user_role.toLowerCase().includes('trainee') ? (
                        <><Trophy size={14} /> Adjudicator</>
                      ) : (
                        <><Users size={14} /> Debater</>
                      )}
                    </div>
                  )}
                  
                  <div className="debate-header">
                    <div className="debate-date">
                      {new Date(latestRound.completed_at).toLocaleDateString()}
                    </div>
                  </div>
                  
                  <div className="debate-motion">
                    <strong>Motion:</strong> {formatMotion(latestRound.motion)}
                  </div>
                  
                  <div className="debate-details">
                    <div className="debate-role">
                      <strong>Role:</strong> {latestRound.user_role}
                    </div>
                    
                    <div className="debate-result">
                      <strong>Result:</strong> {
                        'user_role' in latestRound && (
                          latestRound.user_role.toLowerCase().includes('chair') || 
                          latestRound.user_role.toLowerCase().includes('adjudicator') || 
                          latestRound.user_role.toLowerCase().includes('panelist') || 
                          latestRound.user_role.toLowerCase().includes('trainee')
                        )
                          ? 'winning_side' in latestRound
                            ? `${latestRound.winning_side} Win`
                            : 'N/A'
                          : 'result' in latestRound
                            ? latestRound.result
                            : 'winning_side' in latestRound && 'user_side' in latestRound
                              ? latestRound.winning_side === latestRound.user_side
                                ? 'Won'
                                : 'Lost'
                              : 'N/A'
                      }
                    </div>
                    
                    {('speaker_score' in latestRound && latestRound.speaker_score) && (
                      <div className="debate-score">
                        <strong>Score:</strong> {latestRound.speaker_score}
                      </div>
                    )}
                  </div>
                  
                  <div className="debate-footer">
                    <Button 
                      className="view-details-btn"
                      onClick={() => navigate('/debate-history')}
                      size="sm"
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              ) : (
                !latestRoundLoading && (
                  <div className="no-data-message">
                    <p>You haven't participated in any debates yet.</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
        
        {/* Sidebar Column */}
        <div className="dashboard-sidebar">
          {/* Performance Stats */}
          <div className="card stats-card">
            <div className="role-header">
              <h3>Performance Stats</h3>
              {debateStats || adjudicationStats ? (
                <div className="role-type-badge">
                  {showStatsType === 'debate' ? (
                    <div className="role-badge debater">
                      <Users size={14} /> Debater
                    </div>
                  ) : (
                    <div className="role-badge adjudicator">
                      <Trophy size={14} /> Adjudicator
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            
            {performanceLoading && (
              <div className="loading-indicator">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-t-blue-500 border-b-blue-500 border-opacity-50 mx-auto"></div>
                <p className="text-center mt-2">Loading statistics...</p>
              </div>
            )}
            
            {performanceError && (
              <div className="error-message">
                {performanceError}
              </div>
            )}
            
            {!performanceLoading && !performanceError && (
              <div className="stats-section">
                <div className="fade-transition">
                  {showStatsType === 'debate' ? (
                    <div key={`debate-stats-${animateTrigger}`} className="stat-items fade-enter-active">
                      <div className="stat-item">
                        <div className="stat-value">
                          {debateStats?.total_debates || 0}
                        </div>
                        <div className="stat-label">Total Debates</div>
                      </div>
                      
                      <div className="stat-item">
                        <div className="stat-value">
                          {debateStats?.win_loss?.wins && debateStats?.win_loss?.total_debates 
                            ? `${Math.round((debateStats.win_loss.wins / debateStats.win_loss.total_debates) * 100) || 0}%` 
                            : '0%'}
                        </div>
                        <div className="stat-label">Win Rate</div>
                      </div>
                    
                      <div className="stat-item">
                        <div className="stat-value">
                          {debateStats?.avg_score?.toFixed(1) || '0'}
                        </div>
                        <div className="stat-label">Avg. Speaker Score</div>
                      </div>
                    </div>
                  ) : (
                    <div key={`adjudication-stats-${animateTrigger}`} className="stat-items adjudicator-stats fade-enter-active">
                      <div className="stat-item">
                        <div className="stat-value">
                          {adjudicationStats?.total_rounds || 0}
                        </div>
                        <div className="stat-label">Total Adjudications</div>
                      </div>
                      
                      <div className="stat-item">
                        <div className="stat-value">
                          {adjudicationStats?.avg_rating?.toFixed(1) || '0'}
                        </div>
                        <div className="stat-label">Avg. Rating</div>
                      </div>
                    
                      <div className="stat-item">
                        <div className="stat-value">
                          {Object.keys(adjudicationStats?.roles_distribution || {}).length || 0}
                        </div>
                        <div className="stat-label">Adjudicator Roles</div>
                      </div>
                    </div>
                  )}
                </div>

                <Button 
                  className="btn btn-outline"
                  onClick={() => navigate('/performance')}
                >
                  View Detailed Stats
                </Button>
              </div>
            )}
          </div>

          {/* Role Distribution */}
          <div className="card role-distribution-card">
            <div className="role-header">
              <h3>Role Distribution</h3>
              {(debateStats?.roles_distribution && Object.keys(debateStats.roles_distribution).length > 0) || 
               (adjudicationStats?.roles_distribution && Object.keys(adjudicationStats.roles_distribution).length > 0) ? (
                <div className="role-type-badge">
                  {showRoleStatsType === 'debate' ? (
                    <div className="role-badge debater">
                      <Users size={14} /> Debater
                    </div>
                  ) : (
                    <div className="role-badge adjudicator">
                      <Trophy size={14} /> Adjudicator
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            
            {performanceLoading && (
              <div className="loading-indicator">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-t-blue-500 border-b-blue-500 border-opacity-50 mx-auto"></div>
                <p className="text-center mt-2">Loading role data...</p>
              </div>
            )}
            
            {performanceError && (
              <div className="error-message">
                {performanceError}
              </div>
            )}
            
            {!performanceLoading && !performanceError && (
              <div className="fade-transition">
                {showRoleStatsType === 'debate' && debateStats?.roles_distribution && Object.keys(debateStats.roles_distribution).length > 0 ? (
                  <div className="roles-visualization fade-enter-active">
                    {Object.entries(debateStats.roles_distribution).map(([role, count], index) => {
                      const totalRoles = Object.values(debateStats.roles_distribution).reduce((a, b) => a + b, 0);
                      const percentage = totalRoles > 0 ? Math.round((count / totalRoles) * 100) : 0;
                      // Keep the first color consistent with the badge color (blue for debater)
                      const colors = ['#49a0e8', '#f27474', '#50c878', '#ffa500', '#b07ad6', '#1abc9c'];
                      
                      return (
                        <div key={role} className="role-bar">
                          <div className="role-name">{role}</div>
                          <div className="role-bar-container">
                            <div 
                              className="role-bar-fill" 
                              style={{
                                width: `${percentage}%`,
                                backgroundColor: colors[index % colors.length]
                              }}
                            ></div>
                          </div>
                          <div className="role-count">{count}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : showRoleStatsType === 'adjudication' && adjudicationStats?.roles_distribution && Object.keys(adjudicationStats.roles_distribution).length > 0 ? (
                  <div className="roles-visualization fade-enter-active">
                    {Object.entries(adjudicationStats.roles_distribution).map(([role, count], index) => {
                      const totalRoles = Object.values(adjudicationStats.roles_distribution).reduce((a, b) => a + b, 0);
                      const percentage = totalRoles > 0 ? Math.round((count / totalRoles) * 100) : 0;
                      // Keep the first color consistent with the badge color (purple for adjudicator)
                      const colors = ['#b07ad6', '#f27474', '#50c878', '#ffa500', '#49a0e8', '#1abc9c'];
                      
                      return (
                        <div key={role} className="role-bar">
                          <div className="role-name">{role}</div>
                          <div className="role-bar-container">
                            <div 
                              className="role-bar-fill" 
                              style={{
                                width: `${percentage}%`,
                                backgroundColor: colors[index % colors.length]
                              }}
                            ></div>
                          </div>
                          <div className="role-count">{count}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-data-message fade-enter-active">
                    <p>No role data available yet.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Replace the MainDashboard component with this version
const MainDashboard: React.FC<{ userIsAdmin: boolean }> = ({ userIsAdmin }) => {
  const navigate = useNavigate();
  const { pageTransition } = useTheme(); // Get the current theme

  // Keep all your existing navigation handlers
  const handleNavigateToUserManagement = () => {
    pageTransition(() => navigate("/user-management"));
  };

  const handleNavigateToMotions = () => {
    pageTransition(() => navigate("/admin/motions"));
  };

  const handleNavigateToNewRound = () => {
    pageTransition(() => navigate('/admin/rounds/new'));
  };

  const handleNavigateToRoundManagement = () => {
    pageTransition(() => navigate('/admin/rounds'));
  };
  
  const handleNavigateToNotifications = () => {
    pageTransition(() => navigate('/admin/notifications'));
  };

  // Common button class with improved theme-responsive styling
  const buttonClass = "h-auto py-3 md:py-4 font-medium transition-colors text-sm md:text-base";

  return (
    <motion.div 
      className="p-4 w-full max-w-5xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <h1 className="text-2xl mb-4 font-bold" style={{ color: 'var(--text-color)' }}>
        {userIsAdmin ? "Admin Dashboard" : "Dashboard"}
      </h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
        {/* Combined Card with All Actions */}
        <Card className="shadow-lg col-span-1 md:col-span-2">
          <CardHeader className="pb-1 md:pb-2">
            <CardTitle className="text-lg md:text-xl">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="p-3 md:p-6">
            {/* Join Round Form (for all users) */}
            <div className="mb-4">
              <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-color)' }}>
                Join an active debate round:
              </p>
              <JoinRoundForm />
            </div>
            
            <div className="h-px bg-gray-200 dark:bg-gray-700 my-3"></div>
            
            {/* Common actions grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <Button 
                onClick={() => navigate('/performance')}
                className={buttonClass}
                style={{ backgroundColor: 'var(--primary-color)', color: '#FFFFFF' }}
              >
                <BarChart3 className="mr-2 h-5 w-5" />
                Performance Stats
              </Button>
            </div>
            
            {/* Admin-only actions */}
            {userIsAdmin && (
              <>
                <div className="h-px bg-gray-200 dark:bg-gray-700 my-3"></div>
                <p className="text-sm font-medium dark:text-gray-200 light:text-gray-700 mb-2">
                  Admin Controls:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-4">
                  <Button 
                    onClick={handleNavigateToNewRound}
                    className={`${buttonClass} dark:bg-green-700 light:bg-green-500 dark:hover:bg-green-800 light:hover:bg-green-600 py-3 md:py-4 text-base md:text-lg`}
                  >
                    <PlayCircle className="mr-2 h-5 w-5" />
                    Create New Round
                  </Button>
                  
                  <Button 
                    onClick={handleNavigateToRoundManagement}
                    className={`${buttonClass} dark:bg-amber-700 light:bg-amber-500 dark:hover:bg-amber-800 light:hover:bg-amber-600 py-3 md:py-4 text-base md:text-lg`}
                  >
                    <LayoutGrid className="mr-2 h-5 w-5" />
                    Manage Rounds
                  </Button>
                  
                  <Button 
                    onClick={handleNavigateToUserManagement}
                    className={`${buttonClass} dark:bg-sky-700 light:bg-sky-500 dark:hover:bg-sky-800 light:hover:bg-sky-600 py-3 md:py-4 text-base md:text-lg`}
                  >
                    <Users className="mr-2 h-5 w-5" />
                    User Management
                  </Button>
                  
                  <Button 
                    onClick={handleNavigateToMotions}
                    className={`${buttonClass} dark:bg-emerald-700 light:bg-emerald-500 dark:hover:bg-emerald-800 light:hover:bg-emerald-600 py-3 md:py-4 text-base md:text-lg`}
                  >
                    <BookOpen className="mr-2 h-5 w-5" />
                    Motion Management
                  </Button>
                  
                  <Button 
                    onClick={handleNavigateToNotifications}
                    className={`${buttonClass} dark:bg-rose-700 light:bg-rose-500 dark:hover:bg-rose-800 light:hover:bg-rose-600 py-3 md:py-4 text-base md:text-lg`}
                  >
                    <Bell className="mr-2 h-5 w-5" />
                    Notification Management
                  </Button>
                  
                  {/* ADD THIS: Logs Management Button */}
                  <Button 
                    onClick={() => pageTransition(() => navigate('/admin/logs'))}
                    className={`${buttonClass} dark:bg-amber-600 light:bg-amber-500 dark:hover:bg-amber-700 light:hover:bg-amber-600 py-3 md:py-4 text-base md:text-lg`}
                  >
                    <FileBarChart2 className="mr-2 h-5 w-5" />
                    Activity Logs
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
};

// Add this component after your MainDashboard component
const JoinRoundForm: React.FC = () => {
  const [roundCode, setRoundCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const { pageTransition } = useTheme();

  // Update the handleJoinRound function in your JoinRoundForm component
  const handleJoinRound = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!roundCode.trim()) {
      setError("Please enter a round code");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      
      // Try multiple endpoints for checking round existence
      let roundExists = false;
      let roundData = null;
      
      // Array of endpoints to try for checking if round exists
      const checkEndpoints = [
        `/api/rounds/check/${roundCode.trim()}`,
        `/api/round/check/${roundCode.trim()}`,
        `/rounds/check/${roundCode.trim()}`
      ];
      
      for (const endpoint of checkEndpoints) {
        try {
          console.log(`Trying to check round with endpoint: ${endpoint}`);
          roundData = await fetchWithAuth(endpoint);
          roundExists = true;
          console.log("Round check successful:", roundData);
          break;
        } catch (err) {
          console.log(`Round check failed for endpoint ${endpoint}`);
        }
      }
      
      if (!roundExists) {
        console.log("Round check failed for all endpoints, proceeding directly to join");
      }
      
      // Try multiple endpoints for joining the round
      let joinSuccess = false;
      let response = null;
      
      // Array of endpoints to try for joining
      const joinEndpoints = [
        '/api/rounds/join/',
        '/api/round/join/',
        '/rounds/join/'
      ];
      
      for (const endpoint of joinEndpoints) {
        try {
          console.log(`Trying to join round with endpoint: ${endpoint}`);
          response = await fetchWithAuth(endpoint, {
            method: 'POST',
            body: JSON.stringify({ 
              round_code: roundCode.trim(),
              client_version: "2.0"
            })
          });
          
          joinSuccess = true;
          console.log("Round join successful:", response);
          break;
        } catch (err) {
          console.log(`Join failed for endpoint ${endpoint}`);
        }
      }
      
      if (!joinSuccess) {
        throw new Error("Failed to join round. The round code may be invalid or the round has ended.");
      }

      // Success! Set success message and redirect
      setSuccess("Successfully joined the round!");
      setError("");
      
      // Get the round ID from the response for navigation
      const roundId = response?.round?.id || response?.round_id || response?.id;
      
      if (!roundId) {
        throw new Error("Invalid response from server");
      }
      
      // Redirect to the waiting room for the round
      setTimeout(() => {
        pageTransition(() => {
          navigate(`/round/${roundId}/setup`);
        });
      }, 1000);
      
    } catch (err: any) {
      // Handle specific error cases
      console.error("Error joining round:", err);
      
      if (err.message && err.message.includes("completed")) {
        setError("This round has already been completed and cannot be joined");
      } else if (err.message && err.message.includes("column")) {
        // Database schema error - provide a user-friendly message
        setError("Unable to join round. Please try again or contact support.");
        console.error("Database schema error:", err.message);
      } else {
        setError(err.message || "Failed to join round. Please verify the code is correct.");
      }
      setSuccess("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleJoinRound} className="space-y-4">
      <div>
        <p className="text-sm dark:text-gray-300 light:text-gray-600 mb-4">
          Join an active debate round by entering the round code.
        </p>
        
        {error && (
          <Alert variant="destructive" className="mb-4 py-2">
            <AlertDescription className="text-sm">{error}</AlertDescription>
          </Alert>
        )}
        
        {success && (
          <Alert className="mb-4 py-2 bg-green-500 bg-opacity-10 border border-green-500">
            <AlertDescription className="text-sm text-green-500">{success}</AlertDescription>
          </Alert>
        )}
        
        <div className="flex space-x-2">
          <Input
            value={roundCode}
            onChange={(e) => setRoundCode(e.target.value.toUpperCase())}
            placeholder="Enter round code"
            className="dark:text-white light:text-gray-800 flex-1"
            maxLength={6}
            pattern="[A-Z0-9]{6}"
            title="6-character alphanumeric code"
          />
          
          <Button 
            type="submit"
            className="bg-blue-600 hover:bg-blue-700"
            disabled={loading || !roundCode.trim()}
          >
            {loading ? (
              <>
                <span className="animate-spin mr-2">⟳</span>
                Joining...
              </>
            ) : (
              "Join"
            )}
          </Button>
        </div>
        
        <p className="text-xs text-gray-500 mt-2">
          Round codes are 6-character codes provided by debate administrators.
        </p>
      </div>
    </form>
  );
};

// Practice Materials component
const PracticeMaterials: React.FC = () => {
  return (
    <div className="p-4 w-full max-w-4xl">
      <h1 className="text-3xl mb-6 font-bold dark:text-[#F5F5F5] light:text-gray-900">Practice Materials</h1>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-6">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Speech Structure Exercises</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm dark:text-gray-300 light:text-gray-600 mb-4">
              Learn how to structure compelling arguments and deliver persuasive speeches.
            </p>
            <Button className="w-full bg-purple-600 hover:bg-purple-700">
              View Exercises
            </Button>
          </CardContent>
        </Card>
        
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Rebuttal Techniques</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm dark:text-gray-300 light:text-gray-600 mb-4">
              Practice techniques for addressing and countering opposing arguments effectively.
            </p>
            <Button className="w-full bg-purple-600 hover:bg-purple-700">
              View Techniques
            </Button>
          </CardContent>
        </Card>
        
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Motion Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm dark:text-gray-300 light:text-gray-600 mb-4">
              Learn to break down complex motions and identify key arguments for both sides.
            </p>
            <Button className="w-full bg-purple-600 hover:bg-purple-700">
              View Analysis Guides
            </Button>
          </CardContent>
        </Card>
        
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Video Resources</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm dark:text-gray-300 light:text-gray-600 mb-4">
              Watch recordings of exemplary debate speeches and detailed tutorials.
            </p>
            <Button className="w-full bg-purple-600 hover:bg-purple-700">
              View Videos
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;